import { cn } from "@utils";

// A thin circular gauge around the account's payment score (0–100). Tone follows the same
// thresholds as the FactCells PerformanceScoreCell (red < 34, amber < 67, emerald otherwise)
// so the ring and the Account tab agree. Renders a neutral full ring with no number when the
// score is unknown, so the avatar still reads as an avatar.
export default function ScoreRing({ score, size = 44, strokeWidth = 3, className }) {
  const hasScore = typeof score === "number" && Number.isFinite(score);
  const clamped = hasScore ? Math.max(0, Math.min(100, Math.round(score))) : 0;

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (clamped / 100) * circumference;

  const tone = !hasScore
    ? "text-zinc-300"
    : clamped >= 67
      ? "text-emerald-500"
      : clamped >= 34
        ? "text-amber-500"
        : "text-red-500";

  return (
    <span
      className={cn("relative inline-flex shrink-0 items-center justify-center", className)}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className="stroke-zinc-200"
        />
        {hasScore && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${circumference}`}
            className={cn("transition-[stroke-dasharray] duration-700 ease-out", tone)}
            stroke="currentColor"
          />
        )}
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-zinc-900">
        {hasScore ? clamped : "—"}
      </span>
    </span>
  );
}
